import React, { useEffect, useMemo, useState } from "react"
import {
  map,
  get,
  includes,
  filter,
  find,
  merge,
  cloneDeep,
  isNil,
} from "lodash"
import { changeTerm } from "redux/ducks/Chart"
import { useDispatch, useSelector } from "react-redux"
import { Labels } from "components/atoms/input/input"
import { APIResponse } from "redux/apis/types"
import ClassNames from "classnames"
import {
  calcChangedRatio,
  changeThemeFunction,
  checkIsSp,
  getCountryArea,
  getCountryIcon,
} from "utils/util"
import APIs from "redux/apis"
import * as Formatter from "utils/Formatter"
import MiniChart from "components/atoms/chart/parts/MiniChart"
import useChartInitialize from "components/atoms/chart/parts/useChartInitialize"
import { resultObj } from "components/atoms/chart/logic/chartProvider"
import BigNumber from "bignumber.js"
import {
  indexDataList,
  defaultData,
  requestParam,
  summaryList,
  TERM_EXTERNAL,
  loginErrorResult,
} from "components/pages/others/external/const"
import { changeColor } from "redux/ducks/Setting"
import { UserStorage } from "utils/userStorage"
import {
  PropsType,
  requestParamType,
  chartLegMenu,
} from "components/pages/others/external/utils"
import { Config } from "config/config"
import { useEffectFirstSkip } from "components/actions/customHook/useEffectFirstSkip"

const mainMenu = [{ id: 0, name: "一覧表示" }, { id: 1, name: "チャート表示" }]

export const CreateIndexContents: React.FC<PropsType> = props => {
  const { data, headerOnly, legValue } = props
  const [legValueSp, setLegValueSp] = useState(TERM_EXTERNAL[0].id)
  const [isSpChartOpen, spChartOpen] = useState(false)
  const [chartAreaHeight, setChartAreaHeight] = useState([0, 0])

  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const canvasId = useChartInitialize({
    symbolData: data,
    chartType: "external",
  })
  const symbolDetailSelector = (state: any) =>
    state.chart[canvasId].symbolDetail
  const symbolDetail = useSelector(symbolDetailSelector)

  const countryName = get(data, "DisplayCountryName")
  const _data = headerOnly ? get(data, "Symbol") : symbolDetail
  const icon = getCountryIcon(_data)
  const countryArea = getCountryArea(_data)
  const changedPrice = new BigNumber(get(_data, "Price"))
    .minus(get(_data, "LastClose"))
    .toNumber()
  const changedRatio = calcChangedRatio({
    price: get(_data, "Price"),
    lastClose: get(_data, "LastClose"),
  })

  const dispatch = useDispatch()

  const changeChartLegSP = (id: string) => {
    dispatch(changeTerm(canvasId, Number(id), TERM_EXTERNAL))
    setLegValueSp(Number(id))
  }

  useEffectFirstSkip(() => {
    isPhone && dispatch(changeTerm(canvasId, Number(legValueSp), TERM_EXTERNAL))
  }, [legValueSp])

  useEffectFirstSkip(() => {
    if (isNil(legValue)) return
    dispatch(changeTerm(canvasId, Number(legValue), TERM_EXTERNAL))
  }, [legValue])

  return (
    <>
      <li>
        {!headerOnly &&
          (countryName ? (
            <div className="country-name-cell">{countryName}</div>
          ) : (
            <div className="empty-text" />
          ))}
        <div className="contents-area">
          <div className="list-body price-detail-col price-detail-col-list">
            <div className="price-detail-row">
              <div className={ClassNames("price-detail-title", countryArea)}>
                <div className="index-name-row">
                  <i className={ClassNames("icon-solid ", icon)} />
                  <span className="index-name ellipsis-text">
                    {get(data, "SymbolNameS", "") ||
                      get(_data, "SymbolName", "")}
                  </span>
                </div>
                <div className="time-col">
                  {Formatter.date(get(_data, "PriceTime", ""), "MM/DD HH:mm")}
                </div>
              </div>
              <div
                className={ClassNames("price-detail-value-col right-justify", {
                  "header-only": headerOnly,
                })}
              >
                <div className="price-detail-currency-col">
                  <div className="price-detail-currency-value-col">
                    <div className="price-detail-currency-value">
                      {Formatter.number(get(_data, "Price", null), 2)}
                    </div>
                  </div>
                  <div className="changed-row">
                    <div
                      className={ClassNames(
                        "price-detail-change-price",
                        Formatter.upDown(changedPrice)
                      )}
                    >
                      {Formatter.number(
                        changedPrice,
                        get(_data, "NumberOfDecimals"),
                        "floor",
                        true
                      )}
                    </div>
                    <div className="price-detail-change-ratio">
                      (
                      <span
                        className={ClassNames(Formatter.upDown(changedRatio))}
                      >
                        {Formatter.number(changedRatio, 2, "floor", true)}
                      </span>
                      <span className="unit">%</span>)
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {isPhone && (
              <div className="open-btn-row">
                {isSpChartOpen ? (
                  <div
                    className="btn"
                    onClick={() => {
                      setChartAreaHeight([0, 0])
                      spChartOpen(false)
                    }}
                  >
                    <i className="material-icons">keyboard_arrow_up</i>
                  </div>
                ) : (
                  <div
                    className="btn"
                    onClick={() => {
                      const htmlClass = document.querySelector("html").classList
                      if (includes(htmlClass, "dark")) {
                        ;(async () => {
                          await dispatch(changeColor())
                        })()
                      }
                      dispatch(changeThemeFunction())
                      setChartAreaHeight([415, 205])
                      spChartOpen(true)
                    }}
                  >
                    <i className="material-icons">keyboard_arrow_down</i>
                  </div>
                )}
              </div>
            )}
          </div>
          <div
            className={ClassNames("mini-chart-wrapper", {
              hidden: (isPhone && !isSpChartOpen) || headerOnly,
              isChart: !headerOnly,
            })}
          >
            {!headerOnly && (
              <MiniChart
                canvasId={canvasId}
                miniChartFlag={false}
                priorityFlag={true}
                priorityHeight={!isPhone ? undefined : chartAreaHeight}
              />
            )}
            {isPhone && (
              <Labels
                className="btn-external chart-leg-external-btn sp"
                labelList={chartLegMenu}
                checked={legValueSp}
                onChange={changeChartLegSP}
              />
            )}
          </div>
        </div>
      </li>
    </>
  )
}

export const Market = () => {
  const [legValue, setLegValue] = useState(TERM_EXTERNAL[0].id)
  const [currentDisplay, changeDisplay] = useState(mainMenu[0].id)
  const [indexData, setIndexData] = useState<Array<resultObj>>([defaultData])
  const [mask, setMask] = useState(false)
  const dispatch = useDispatch()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])

  setInterval(function() {
    postSize()
  }, 100)

  const postSize = () => {
    window.parent.postMessage(
      document.getElementById("root").clientHeight,
      Config.api.iframeTargetOrigin
    )
  }

  const callApi = async (
    params: Array<requestParamType>,
    setData: Function
  ) => {
    const res: APIResponse.Res = await Promise.all(
      map(params, async param => {
        const delayChartData = await APIs.Chart.DelayChart({
          ...param,
          LegType: "1D",
        })
        const _indexData = find(indexDataList, { Symbol: get(param, "Symbol") })
        const _delayChartData = cloneDeep(delayChartData)
        merge(get(_delayChartData, "Symbol"), _indexData)
        return _delayChartData
      })
    )
    setData(res)
  }

  useEffect(() => {
    ;(async () => {
      const res = await APIs.Account.OpenLogin()
      if (get(res, "Result") === 0) {
        setMask(false)
        await UserStorage.User.set(res)
        const htmlClass = document.querySelector("html").classList
        if (includes(htmlClass, "dark")) {
          ;(async () => {
            await dispatch(changeColor())
            dispatch(changeThemeFunction())
          })()
        }
        !isPhone && callApi(requestParam, setIndexData)
      }
      //ログインができない時間帯(2時から6時の間)はマスクをかけて触れないようにする。
      if (get(res, "Result") === loginErrorResult) {
        setMask(true)
      }
    })()
  }, [])

  useEffectFirstSkip(() => {
    if (currentDisplay === 1 || isPhone) return
    callApi(requestParam, setIndexData)
  }, [currentDisplay])

  return (
    <div className="external-contents">
      {mask && <div className="mask">現在チャートは表示できません</div>}
      {!isPhone ? (
        <>
          <div className="control-row">
            <div className="main-toggle">
              <Labels
                className="btn-external main-external-btn"
                labelList={mainMenu}
                checked={currentDisplay}
                onChange={changeDisplay}
              />
            </div>
            <div className="chart-leg">
              <Labels
                className="btn-external chart-leg-external-btn"
                labelList={chartLegMenu}
                checked={legValue}
                onChange={(id: string) => setLegValue(Number(id))}
                disabled={!currentDisplay}
              />
            </div>
          </div>
          {!currentDisplay ? (
            <div className="summary">
              <div className="world-map" />
              {map(
                summaryList,
                (
                  list: {
                    Name: string
                    CountryClass: string
                    Exchange: Array<number>
                  },
                  i: number
                ) => {
                  return (
                    <ul
                      className={ClassNames(
                        "chart-list-contents chart-list thin chart-mini-contents",
                        list.CountryClass
                      )}
                      key={i}
                    >
                      <div className="country-name">{list.Name}</div>
                      {map(
                        filter(indexData, (data: object) => {
                          return includes(
                            list.Exchange,
                            get(data, "Symbol.Exchange")
                          )
                        }),
                        (data: resultObj, key: number) => {
                          return (
                            <CreateIndexContents
                              key={key}
                              data={data}
                              headerOnly={true}
                            />
                          )
                        }
                      )}
                    </ul>
                  )
                }
              )}
            </div>
          ) : (
            <div className="chart">
              <ul className="chart-list-contents chart-list thin chart-mini-contents">
                {map(indexDataList, (data: resultObj, key: number) => (
                  <CreateIndexContents
                    key={key}
                    data={data}
                    headerOnly={false}
                    legValue={legValue}
                  />
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div>
          <ul className="chart-list-contents chart-list thin chart-mini-contents">
            {map(indexDataList, (data: resultObj, key: number) => (
              <CreateIndexContents key={key} data={data} headerOnly={false} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
